import { Route, Routes, useLocation } from "react-router";
import { AppAuthGuard, WorkspacePermissionGuard } from "@/routes/guards";
import { WorkspacePermissions } from "@/core/permissions/Workspace.permissions";
import WorkspaceRootLayout from "@/presentation/ui/layouts/workspace/WorkspaceRoot.layout";
import { WORKSPACE_ROUTES } from "./utils/Workspace.router.utils";
import { lazy } from "react";
import NotFound from "@/presentation/pages/NotFound";

const DashboardPage = lazy(() => import("@/presentation/pages/dashboard/Dashboard.page"));
const HomePage = lazy(() => import("@/presentation/pages/home/Home.page"));
const DeductionPage = lazy(() => import("@/presentation/pages/deductions/Deduction.page"));
const IntelliUploadPage = lazy(() => import("@/presentation/pages/intelli-upload/Intelli-upload.page"));
const ConversationsPage = lazy(() => import("@/presentation/pages/conversations/Conversations.page"));
const UsersPage = lazy(() => import("@/presentation/pages/users/Users.page"));
const AnalyticsPage = lazy(() => import("@/presentation/pages/analytics/Analytics.page"));
const PurgeDataPage = lazy(() => import("@/presentation/pages/purge-data/purge-data.page"));
const ApprovalsPage = lazy(() => import("@/presentation/pages/approvals/Approvals.page"));
const AccountingPage = lazy(() => import("@/presentation/pages/accounting/Accounting.page"));
const TradePromotionsPage = lazy(() => import("@/presentation/pages/trade-promotions/TradePromotions.page"));
const DisputeTrackingPage = lazy(() => import("@/presentation/pages/dispute-tracking/DisputeTracking.page"));
const DocumentRepositoryPage = lazy(() => import("@/presentation/pages/document-repository/DocumentRepository.page"));

const WorkspaceRoutes = () => {
  const location = useLocation();

  return (
    <Routes location={location} key={"/workspace"}>
      <Route path={WORKSPACE_ROUTES.HOME} element={<WorkspaceRootLayout />}>
        <Route index element={<DashboardPage />} />
        <Route
          path={WORKSPACE_ROUTES.HOME_TODO}
          element={
            <WorkspacePermissionGuard requiredPermissions={[WorkspacePermissions.HOME_TODO_VIEW]}>
              <HomePage />
            </WorkspacePermissionGuard>
          }
        />
        <Route
          path={WORKSPACE_ROUTES.DEDUCTIONS}
          element={
            <WorkspacePermissionGuard requiredPermissions={[WorkspacePermissions.DEDUCTIONS_VIEW]}>
              <DeductionPage />
            </WorkspacePermissionGuard>
          }
        />
        <Route
          path={WORKSPACE_ROUTES.APPROVALS}
          element={
            <WorkspacePermissionGuard requiredPermissions={[WorkspacePermissions.DEDUCTIONS_APPROVAL_VIEW]}>
              <ApprovalsPage />
            </WorkspacePermissionGuard>
          }
        />
        <Route
          path={WORKSPACE_ROUTES.INTELLI_UPLOAD}
          element={
            <WorkspacePermissionGuard requiredPermissions={[WorkspacePermissions.INTELLI_UPLOAD_UPLOAD]}>
              <IntelliUploadPage />
            </WorkspacePermissionGuard>
          }
        />
        <Route
          path={WORKSPACE_ROUTES.CONVERSATIONS}
          element={
            <WorkspacePermissionGuard requiredPermissions={[WorkspacePermissions.CONVERSATIONS_VIEW]}>
              <ConversationsPage />
            </WorkspacePermissionGuard>
          }
        />
        <Route
          path={WORKSPACE_ROUTES.ACCOUNTING}
          element={
            <WorkspacePermissionGuard
              requiredPermissions={[
                WorkspacePermissions.ACCOUNTING_CREDIT_MEMO_VIEW,
                WorkspacePermissions.ACCOUNTING_JOURNAL_ENTRY_VIEW,
                WorkspacePermissions.ACCOUNTING_VIEW,
              ]}
            >
              <AccountingPage />
            </WorkspacePermissionGuard>
          }
        />
        <Route
          path={WORKSPACE_ROUTES.TRADE_PROMOTIONS}
          element={
            <WorkspacePermissionGuard requiredPermissions={[WorkspacePermissions.TRADE_PROMOTIONS_VIEW]}>
              <TradePromotionsPage />
            </WorkspacePermissionGuard>
          }
        />
        <Route
          path={WORKSPACE_ROUTES.DISPUTES_TRACKING}
          element={
            <WorkspacePermissionGuard requiredPermissions={[WorkspacePermissions.DISPUTES_TRACKING_VIEW]}>
              <DisputeTrackingPage />
            </WorkspacePermissionGuard>
          }
        />
        <Route
          path={WORKSPACE_ROUTES.DOCUMENT_REPOSITORY}
          element={
            <WorkspacePermissionGuard requiredPermissions={[WorkspacePermissions.DOCUMENT_REPOSITORY_VIEW]}>
              <DocumentRepositoryPage />
            </WorkspacePermissionGuard>
          }
        />
        <Route
          path={WORKSPACE_ROUTES.USERS}
          element={
            <WorkspacePermissionGuard requiredPermissions={[WorkspacePermissions.USER_MANAGEMENT_VIEW]}>
              <UsersPage />
            </WorkspacePermissionGuard>
          }
        />
        <Route
          path={WORKSPACE_ROUTES.ANALYTICS}
          element={
            <WorkspacePermissionGuard requiredPermissions={[WorkspacePermissions.ANALYTICS_VIEW]}>
              <AnalyticsPage />
            </WorkspacePermissionGuard>
          }
        />
        <Route
          path={WORKSPACE_ROUTES.PURGE_DATA}
          element={
            <WorkspacePermissionGuard requiredPermissions={[WorkspacePermissions.PURGE_TRANSACTIONAL_DATA]}>
              <PurgeDataPage />
            </WorkspacePermissionGuard>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const WorkspaceAuthGuardWrapper = () => {
  return (
    <AppAuthGuard>
      <WorkspaceRoutes />
    </AppAuthGuard>
  );
};

export default WorkspaceAuthGuardWrapper;
